import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';

import { FormattedMessage } from '../../../util/reactIntl';
import { useIsInTwinrApp } from '../../../util/twinrApp';
import { ExternalLink, NamedLink } from '../../../components';
import {
  DIGO_CLIENT_USER_TYPE,
  DIGO_WORKER_USER_TYPE,
  browseWorkersPageLinkProps,
  browseContractsLinkProps,
  newRequestQuoteLinkProps,
  newServiceOfferLinkProps,
  signupLinkProps,
  workerNearbySearchLinkProps,
} from '../digoServices';
import css from './SectionHeroDigo.module.css';

// Photos saisonnières (automne). `position` = object-position CSS : garde la
// personne dans le cadre quand le panneau est plus étroit que la photo (3:2).
// Les photos d'été (digo-home-hero-1 à 4) restent dans public/static.
const HERO_PHOTOS = [
  {
    src: '/static/digo-home-hero-automne-gouttieres.webp',
    alt: 'Travailleur Digo en t-shirt violet nettoyant des gouttières remplies de feuilles mortes',
    position: '80% center',
  },
  {
    src: '/static/digo-home-hero-automne-abri-tempo.webp',
    alt:
      "Deux travailleurs Digo en t-shirt violet installant un abri d'auto devant une maison en automne",
    position: '80% center',
  },
  {
    src: '/static/digo-home-hero-automne-pneus.webp',
    alt: "Travailleur Digo en t-shirt violet faisant le changement de pneus d'un VUS",
    position: '45% center',
  },
  {
    src: '/static/digo-home-hero-automne-piscine.webp',
    alt: 'Travailleur Digo en t-shirt violet nettoyant une piscine creusée avant sa fermeture',
    position: '75% center',
  },
];
const HERO_SLIDESHOW_INTERVAL_MS = 4000;

// URLs des apps Digo sur les stores (les deux sont en ligne).
const IOS_APP_URL = 'https://apps.apple.com/ca/app/digo/id6795388445';
const ANDROID_APP_URL = 'https://play.google.com/store/apps/details?id=ca.digo.app';

const IOS_COMING_SOON = false;
const ANDROID_COMING_SOON = false;

const AppleIcon = () => (
  <svg viewBox="0 0 24 24" className={css.appStoreIcon} aria-hidden="true">
    <path
      fill="currentColor"
      d="M16.365 12.146c-.02-2.117 1.728-3.13 1.806-3.178-1.003-1.462-2.564-1.662-3.117-1.683-1.316-.134-2.577.775-3.244.775-.668 0-1.693-.756-2.786-.736-1.434.02-2.77.833-3.51 2.117-1.5 2.6-.384 6.45 1.07 8.56.714 1.03 1.56 2.19 2.676 2.15 1.08-.043 1.485-.697 2.787-.697 1.302 0 1.67.697 2.808.676 1.16-.02 1.896-1.05 2.604-2.083.82-1.198 1.157-2.36 1.175-2.418-.026-.012-2.257-.866-2.279-3.436zm-2.04-6.52c.59-.714 1.004-1.706.894-2.693-.864.035-1.91.576-2.53 1.29-.548.63-1.028 1.64-.9 2.606.95.073 1.918-.483 2.536-1.203z"
    />
  </svg>
);

const PlayStoreIcon = () => (
  <svg viewBox="0 0 24 24" className={css.appStoreIcon} aria-hidden="true">
    <path fill="currentColor" d="M4 3.5v17l14-8.5L4 3.5z" />
  </svg>
);

// ─── Icônes du hero connecté ───────────────────────────
const ActionSearchIcon = () => (
  <svg className={css.actionSvg} viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="11" cy="11" r="7" />
    <line x1="21" y1="21" x2="16.5" y2="16.5" />
  </svg>
);

const ActionDocIcon = () => (
  <svg className={css.actionSvg} viewBox="0 0 24 24" aria-hidden="true">
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z" />
    <line x1="9" y1="14" x2="15" y2="14" />
    <line x1="12" y1="11" x2="12" y2="17" />
  </svg>
);

const ActionChevron = () => (
  <svg className={css.actionChevron} viewBox="0 0 24 24" aria-hidden="true">
    <polyline points="9 6 15 12 9 18" />
  </svg>
);

const ToggleClientIcon = () => (
  <svg className={css.toggleIcon} viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="9" cy="8" r="3.1" />
    <path d="M3.6 19c.6-3 2.8-4.6 5.4-4.6s4.8 1.6 5.4 4.6" />
    <path d="M16 5.3a3 3 0 0 1 0 5.6" />
    <path d="M17.8 14.7c1.9.5 3.2 1.9 3.6 4.3" />
  </svg>
);

const ToggleWorkIcon = () => (
  <svg className={css.toggleIcon} viewBox="0 0 24 24" aria-hidden="true">
    <rect x="3.5" y="7.5" width="17" height="12" rx="2" />
    <path d="M9 7.5V6a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v1.5" />
    <line x1="3.5" y1="13" x2="20.5" y2="13" />
  </svg>
);

const GuestSignupCtas = () => {
  const clientSignup = signupLinkProps(DIGO_CLIENT_USER_TYPE);
  const workerSignup = signupLinkProps(DIGO_WORKER_USER_TYPE);

  return (
    <div className={css.guestCtas}>
      <NamedLink name={clientSignup.name} to={clientSignup.to} className={css.createListingCta}>
        <FormattedMessage id="SectionHeroDigo.publishListing" />
      </NamedLink>
      <NamedLink
        name={workerSignup.name}
        to={workerSignup.to}
        className={classNames(css.createListingCta, css.guestCtaSecondary)}
      >
        <FormattedMessage id="SectionHeroDigo.becomeWorker" />
      </NamedLink>
    </div>
  );
};

// ─── Composant principal ───────────────────────────────
const SectionHeroDigo = () => {
  const currentUser = useSelector(state => state.user?.currentUser);
  const isAuthenticated = useSelector(state => state.auth?.isAuthenticated);
  const userType = currentUser?.attributes?.profile?.publicData?.userType;

  const isWorker = userType === 'provider';
  const isGuest = !isAuthenticated;
  const isClient = isAuthenticated && !isWorker;
  const isInApp = useIsInTwinrApp();
  const createListingLink = newRequestQuoteLinkProps();
  const nearbySearchLink = workerNearbySearchLinkProps();
  const serviceOfferLink = newServiceOfferLinkProps();
  const browseWorkersPageLink = browseWorkersPageLinkProps();
  const browseContractsLink = browseContractsLinkProps();
  // Worker home toggle: 'client' (I need help) or 'worker' (I want to work).
  const [workerIntent, setWorkerIntent] = useState('client');
  const [heroIndex, setHeroIndex] = useState(0);

  useEffect(() => {
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    if (prefersReducedMotion) {
      return undefined;
    }

    const id = window.setInterval(() => {
      setHeroIndex(current => (current + 1) % HERO_PHOTOS.length);
    }, HERO_SLIDESHOW_INTERVAL_MS);

    return () => window.clearInterval(id);
  }, []);

  // ─── Hero connecté (client ou travailleur) : titre + (toggle) + 2 actions ───
  if (isAuthenticated) {
    const intent = isWorker ? workerIntent : 'client';
    const isWorkerMode = intent === 'worker';

    const actions = isWorkerMode
      ? [
          {
            key: 'find-work',
            primary: true,
            icon: <ActionSearchIcon />,
            titleId: 'SectionHeroDigo.findWorkTitle',
            subId: 'SectionHeroDigo.findWorkSub',
            link: browseContractsLink,
          },
          {
            key: 'post-offer',
            primary: false,
            icon: <ActionDocIcon />,
            titleId: 'SectionHeroDigo.postOfferTitle',
            subId: 'SectionHeroDigo.postOfferSub',
            link: serviceOfferLink,
          },
        ]
      : [
          {
            key: 'find-someone',
            primary: true,
            icon: <ActionSearchIcon />,
            titleId: 'SectionHeroDigo.findSomeoneTitle',
            subId: 'SectionHeroDigo.findSomeoneSub',
            link: browseWorkersPageLink,
          },
          {
            key: 'post-request',
            primary: false,
            icon: <ActionDocIcon />,
            titleId: 'SectionHeroDigo.postRequestTitle',
            subId: 'SectionHeroDigo.postRequestSub',
            link: createListingLink,
          },
        ];

    const subtitleId = isWorkerMode
      ? 'SectionHeroDigo.workerSubtitle'
      : 'SectionHeroDigo.clientSubtitle';

    return (
      <section className={css.root}>
        <div className={css.loggedHero}>
          <div className={css.loggedInner}>
            <h1 className={css.loggedTitle}>
              <FormattedMessage id="SectionHeroDigo.loggedTitle" />
            </h1>

            {isWorker ? (
              <div className={css.intentToggle} role="tablist" aria-label="Que voulez-vous faire">
                <button
                  type="button"
                  role="tab"
                  aria-selected={!isWorkerMode}
                  className={classNames(css.intentOption, {
                    [css.intentOptionActive]: !isWorkerMode,
                  })}
                  onClick={() => setWorkerIntent('client')}
                >
                  <ToggleClientIcon />
                  <FormattedMessage id="SectionHeroDigo.toggleClient" />
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={isWorkerMode}
                  className={classNames(css.intentOption, {
                    [css.intentOptionActive]: isWorkerMode,
                  })}
                  onClick={() => setWorkerIntent('worker')}
                >
                  <ToggleWorkIcon />
                  <FormattedMessage id="SectionHeroDigo.toggleWorker" />
                </button>
              </div>
            ) : null}

            <p className={css.loggedSubtitle}>
              <FormattedMessage id={subtitleId} />
            </p>

            <div className={css.actionButtons}>
              {actions.map(action => (
                <NamedLink
                  key={action.key}
                  name={action.link.name}
                  params={action.link.params}
                  to={action.link.to}
                  className={classNames(css.actionButton, {
                    [css.actionPrimary]: action.primary,
                    [css.actionSecondary]: !action.primary,
                  })}
                >
                  <span className={css.actionIconCircle}>{action.icon}</span>
                  <span className={css.actionText}>
                    <span className={css.actionTitle}>
                      <FormattedMessage id={action.titleId} />
                    </span>
                    <span className={css.actionSub}>
                      <FormattedMessage id={action.subId} />
                    </span>
                  </span>
                  <ActionChevron />
                </NamedLink>
              ))}
            </div>
          </div>

          {!isInApp && (
            <div className={css.loggedPhoto}>
              {HERO_PHOTOS.map((photo, index) => (
                <img
                  key={photo.src}
                  src={photo.src}
                  alt={index === heroIndex ? photo.alt : ''}
                  className={classNames(css.photo, { [css.photoVisible]: index === heroIndex })}
                  style={photo.position ? { objectPosition: photo.position } : undefined}
                  aria-hidden={index !== heroIndex}
                  loading="lazy"
                />
              ))}
            </div>
          )}
        </div>
      </section>
    );
  }

  return (
    <section className={css.root}>
      <div className={classNames(css.container, { [css.containerLean]: isInApp })}>
        {/* Panneau gauche — texte sur fond lavande */}
        <div className={classNames(css.leftPanel, { [css.leftPanelLean]: isInApp })}>
          <h1 className={css.title}>
            Le temps <span className={css.strike}>ne</span>
            <br />
            s&apos;achète{' '}
            <span className={css.strikeWrap}>
              <span className={css.strike}>pas</span>.
            </span>
          </h1>

          {isWorker ? (
            <p className={css.subtitle}>
              Des clients de partout au Québec publient leurs mandats chaque jour.
            </p>
          ) : (
            <p className={css.subtitle}>
              {isGuest
                ? 'Inscris-toi, décris ta tâche, on trouve un travailleur certifié Digo près de chez toi.'
                : 'Décris ta tâche, on trouve un travailleur vérifié près de chez toi.'}
            </p>
          )}

          <div className={css.searchOverlay}>
            {isWorker ? (
              <div className={css.guestCtas}>
                <NamedLink
                  name={nearbySearchLink.name}
                  to={nearbySearchLink.to}
                  className={css.createListingCta}
                >
                  <FormattedMessage id="SectionHeroDigo.browseOffers" />
                </NamedLink>
                <NamedLink
                  name={serviceOfferLink.name}
                  params={serviceOfferLink.params}
                  to={serviceOfferLink.to}
                  className={classNames(css.createListingCta, css.guestCtaSecondary)}
                >
                  <FormattedMessage id="SectionHeroDigo.offerMyService" />
                </NamedLink>
              </div>
            ) : isClient ? (
              <NamedLink
                name={createListingLink.name}
                params={createListingLink.params}
                to={createListingLink.to}
                className={css.createListingCta}
              >
                <FormattedMessage id="SectionHeroDigo.createListing" />
              </NamedLink>
            ) : (
              <GuestSignupCtas />
            )}
          </div>
        </div>

        {/* Panneau droit — photo avec coins arrondis.
          Dans l'app (Twinr), on retire l'image pour un hero plus compact. */}
        {!isInApp && (
          <div className={css.rightPanel}>
            <div className={css.photoWrapper}>
              {HERO_PHOTOS.map((photo, index) => (
                <img
                  key={photo.src}
                  src={photo.src}
                  alt={index === heroIndex ? photo.alt : ''}
                  className={classNames(css.photo, { [css.photoVisible]: index === heroIndex })}
                  style={photo.position ? { objectPosition: photo.position } : undefined}
                  aria-hidden={index !== heroIndex}
                />
              ))}
              {!isInApp && (
                <div className={css.appStoreBadges}>
                  {IOS_COMING_SOON ? (
                    <div
                      className={css.appStoreBadgeDisabled}
                      aria-label="App Store — bientôt disponible"
                    >
                      <AppleIcon />
                      <span className={css.appStoreText}>
                        <span className={css.appStoreLabel}>App Store</span>
                        <span className={css.appStoreName}>Bientôt disponible</span>
                      </span>
                    </div>
                  ) : (
                    <ExternalLink
                      href={IOS_APP_URL}
                      className={css.appStoreBadge}
                      aria-label="Télécharger dans l'App Store"
                    >
                      <AppleIcon />
                      <span className={css.appStoreText}>
                        <span className={css.appStoreLabel}>Télécharger dans l&apos;</span>
                        <span className={css.appStoreName}>App Store</span>
                      </span>
                    </ExternalLink>
                  )}
                  {ANDROID_COMING_SOON ? (
                    <div
                      className={css.appStoreBadgeDisabled}
                      aria-label="Google Play — bientôt disponible"
                    >
                      <PlayStoreIcon />
                      <span className={css.appStoreText}>
                        <span className={css.appStoreLabel}>Google Play</span>
                        <span className={css.appStoreName}>Bientôt disponible</span>
                      </span>
                    </div>
                  ) : (
                    <ExternalLink
                      href={ANDROID_APP_URL}
                      className={css.appStoreBadge}
                      aria-label="Disponible sur Google Play"
                    >
                      <PlayStoreIcon />
                      <span className={css.appStoreText}>
                        <span className={css.appStoreLabel}>Disponible sur</span>
                        <span className={css.appStoreName}>Google Play</span>
                      </span>
                    </ExternalLink>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default SectionHeroDigo;
